import React, {useEffect, useMemo, useRef, useState} from "react";
import {useNavigate, useParams, useSearchParams} from "react-router-dom";
import {Box, Grid, Paper, TextField, Typography, Button, MenuItem} from "@mui/material";
import {useMutation, useQuery} from "@apollo/client";
import DOMPurify from "dompurify";

// ---- Reuse your existing GQL ----
import {EMAIL_TEMPLATE_BY_ID, EMAIL_TEMPLATES, UPSERT_EMAIL_TEMPLATE} from "../gql/emailTemplates.gql";
import {withAuthenticationRequired} from "@auth0/auth0-react";
import Loading from "../components/common/Loading";
import {useTranslation} from "react-i18next";
import {useTenantConfig} from "../security/tenant-config-context";
import AppSnackbar from "../common/AppSnackbar";
import AlertDialogSlide from "../components/common/AlertDialogSlide";
import {MenuButtonInsertPlaceholder} from "./editor/components/MenuButtonInsertPlaceholder";
import {
    exportToTemplateString,
    migrateLegacyPlaceholders,
    renderTemplate as renderContractTemplate
} from "./editor/utils/contractTemplateUtils";
import {useEditor} from "@tiptap/react";
import {StarterKit} from "@tiptap/starter-kit";
import {Underline} from "@tiptap/extension-underline";
import {
    MenuButtonBold,
    MenuButtonBulletedList, MenuButtonItalic, MenuButtonOrderedList, MenuButtonRedo, MenuButtonTextColor,
    MenuButtonUnderline, MenuButtonUndo,
    MenuControlsContainer, MenuDivider,
    MenuSelectHeading,
    RichTextField,
    RichTextEditorProvider
} from "mui-tiptap";
import {Placeholder} from "./editor/extensions/Placeholder";
import {Color} from "@tiptap/extension-color";
import {TextStyle} from "@tiptap/extension-text-style";

// ---- Params & helpers (kopieer uit je werkende component) ----
const RESERVATION_EMAIL_TYPES = [
    "RESERVATION_CONFIRMATION",
    "RESERVATION_REMINDER",
];
const APPOINTMENT_EMAIL_TYPES = [
    "APPOINTMENT_CONFIRMATION",
    "APPOINTMENT_REMINDER",
];

// Placeholders (groepeer per business flow)
const COMMON_PARAMS = [
    "CUSTOMER_NAME",
];

const RESERVATION_PARAMS = [
    "ANIMAL_SEX",
    "ANIMAL_BIRTHDATE",
    "CHECK_IN_DATE",
    "CHECK_OUT_DATE",
    "CHECK_IN_HOUR",
    "CHECK_OUT_HOUR",
    "ANIMAL_NAMES_IN_RESERVATION",
    "RESERVATION_NUMBER",
    "RESERVATION_TOTAL_AMOUNT",
    "RESERVATION_ADVANCE",
    "RESERVATION_REMAINING_AMOUNT",
];

const APPOINTMENT_PARAMS = [
    "SERVICE_START",
    "SERVICE_END",
    "APPOINTMENT_NUMBER",
    "ANIMAL_NAMES_IN_APPOINTMENT",
    "SERVICE_DESCRIPTION",
    "APPOINTMENT_TOTAL_AMOUNT",
    "APPOINTMENT_ADVANCE",
    "APPOINTMENT_REMAINING_AMOUNT",
    "APPOINTMENT_NOTE",
];

function renderTemplate(body, values) {
    if (!body) return "";
    return renderContractTemplate(body, values);
}

const EmailTemplateEditor = () => {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const {id} = useParams();
    const [searchParams] = useSearchParams();
    const type = searchParams.get("type");
    const {cfg} = useTenantConfig();

    const isAppointmentFlow = cfg?.businessTypes?.includes('APPOINTMENT_BASED');
    const VISIBLE_EMAIL_TYPES = useMemo(() => {
        // Appointment-based tenants use appointment templates; others default to reservation templates
        return isAppointmentFlow ? APPOINTMENT_EMAIL_TYPES : RESERVATION_EMAIL_TYPES;
    }, [isAppointmentFlow]);
    const VISIBLE_PARAMS = useMemo(() => {
        // Default to reservation placeholders if unknown
        const specific = isAppointmentFlow ? APPOINTMENT_PARAMS : RESERVATION_PARAMS;
        return [...COMMON_PARAMS, ...specific];
    }, [isAppointmentFlow]);

    const {data, loading} = useQuery(EMAIL_TEMPLATE_BY_ID, {
        variables: {id: Number(id), type: type},
    });
    const [upsert] = useMutation(UPSERT_EMAIL_TEMPLATE, {
        refetchQueries: [{query: EMAIL_TEMPLATES}],
        awaitRefetchQueries: true,
    });

    const existing = data?.emailTemplateById;

    // Form
    const [form, setForm] = useState({
        emailType: existing?.emailType || "",
        replyTo: existing?.replyTo || "",
        daysBeforeReservation: existing?.daysBeforeReservation || 1,
        subject: existing?.subject || "",
        body: existing?.body || "",
    });

    const isProgrammaticSetRef = useRef(false);
    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                heading: {levels: [1, 2]},
            }),
            Underline,
            TextStyle,
            Color,
            Placeholder,
        ],
        content: migrateLegacyPlaceholders(existing?.body ?? ""),
        onUpdate: ({editor}) => {
            if (isProgrammaticSetRef.current) return;
            setForm(f => ({...f, body: editor.getHTML()}));
            setDirty(true);
        },
    });

    useEffect(() => {
        if (existing) {
            setForm({
                emailType: existing.emailType,
                replyTo: existing.replyTo ?? "",
                subject: existing.subject ?? "",
                body: migrateLegacyPlaceholders(existing.body ?? ""),
                daysBeforeReservation: existing.daysBeforeReservation ?? 1,
            });
            if (editor && existing.body) {
                isProgrammaticSetRef.current = true;
                editor.commands.setContent(migrateLegacyPlaceholders(existing.body));
                isProgrammaticSetRef.current = false;
            }
        }
    }, [existing, editor]);

    const [dirty, setDirty] = useState(false);
    const onChange = (e) => {
        setDirty(true);
        setForm(value => ({...value, [e.target.name]: e.target.name === 'daysBeforeReservation' ? Number(e.target.value) : e.target.value}));
    };
    const isValid = useMemo(() => form.emailType && form.subject.trim() && form.body.trim() && form.body.trim() !== '<p></p>', [form]);

    const [previewValues, setPreviewValues] = useState({
        // Common
        CUSTOMER_NAME: "John Doe",
        // Reservation examples
        ANIMAL_SEX: "V",
        ANIMAL_BIRTHDATE: "12-08-2020",
        CHECK_IN_DATE: "01-01-2025",
        CHECK_OUT_DATE: "31-12-2025",
        CHECK_IN_HOUR: "09:00",
        CHECK_OUT_HOUR: "17:00",
        ANIMAL_NAMES_IN_RESERVATION: "Dier 1, Dier 2",
        RESERVATION_NUMBER: "R-2025-0001",
        RESERVATION_STAY: "Suite • 2 katten",
        RESERVATION_TOTAL_AMOUNT: "€ 120,00",
        RESERVATION_ADVANCE: "€ 30,00",
        RESERVATION_REMAINING_AMOUNT: "€ 90,00",
        // Appointment examples
        SERVICE_START: "01-02-2025 10:00",
        SERVICE_END: "01-02-2025 11:15",
        APPOINTMENT_NUMBER: "A-2025-0456",
        ANIMAL_NAMES_IN_APPOINTMENT: "Minoes",
        SERVICE_DESCRIPTION: "Volledige trimbeurt",
        APPOINTMENT_TOTAL_AMOUNT: "€ 65,00",
        APPOINTMENT_ADVANCE: "€ 0,00",
        APPOINTMENT_REMAINING_AMOUNT: "€ 65,00",
        APPOINTMENT_NOTE: "Graag rustige aanpak",
    });
    // Save / Cancel
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [severity, setSeverity] = useState("success");
    const handleSave = async () => {
        if (!isValid) return;
        try {
            const input = {
                ...form,
                body: exportToTemplateString(form.body)
            };
            await upsert({variables: {input: input}});
            setDirty(false);
            setSnackbarMessage(t("contractTemplateSaved"));
            setSeverity("success");
            setOpenSnackbar(true);
            navigate("/beheer/configuratie?tab=emails");
        } catch {
            setSnackbarMessage(t("saveFailed"));
            setSeverity("error");
            setOpenSnackbar(true);
        }
    };
    // Confirm cancel dialog
    const [openAlert, setOpenAlert] = useState(false);
    const [alertTitle, setAlertTitle] = useState("");
    const [alertMessage, setAlertMessage] = useState("");
    const [alertAction, setAlertAction] = useState("");

    const resetAlert = () => {
        setOpenAlert(false);
        setAlertTitle("");
        setAlertMessage("");
        setAlertAction("");
    };

    const handleCancel = () => {
        if (dirty) {
            setAlertTitle(t('confirm'));
            setAlertMessage(t('ignoreChanges'));
            setAlertAction('CANCEL_EDIT');
            setOpenAlert(true);
            return;
        }
        navigate("/beheer/configuratie?tab=emails");
    };

    const handleCloseAlert = (result, action) => {
        setOpenAlert(false);
        if (result && action === 'CANCEL_EDIT') {
            resetAlert();
            navigate("/beheer/configuratie?tab=emails");
        } else {
            resetAlert();
        }
    };

    // Guard (optioneel): warn on unload
    useEffect(() => {
        const onBeforeUnload = (e) => {
            if (dirty) {
                e.preventDefault();
                e.returnValue = "";
            }
        };
        window.addEventListener("beforeunload", onBeforeUnload);
        return () => window.removeEventListener("beforeunload", onBeforeUnload);
    }, [dirty]);

    return (
        <Box sx={{width: "100%", paddingLeft: "100px"}}>
            {/* Sticky header */}
            <Box sx={{
                position: "sticky", top: 0, zIndex: 2, bgcolor: "background.paper",
                borderBottom: "1px solid", borderColor: "divider", p: 2,
                display: "flex", alignItems: "center", justifyContent: "space-between"
            }}>
                <Typography variant="h5">{id ? t("editTemplate") : t("newTemplate")}</Typography>
                <Box sx={{display: "flex", gap: 1}}>
                    <Button onClick={handleCancel} data-testid="cancel-email-button">{t('cancel')}</Button>
                    <Button variant="contained" onClick={handleSave} disabled={!isValid} data-testid="save-email-button">{t('save')}</Button>
                </Box>
            </Box>

            <Box sx={{paddingTop: 2}}>
                <Grid container spacing={2}>
                    {/* Linker kolom */}
                    <Grid item xs={12} md={6}>
                        <Paper variant="outlined" sx={{p: 2}}>
                            <TextField
                                disabled={!!existing?.id}
                                select fullWidth required margin="normal"
                                label={t('emailType')} name="emailType" value={form.emailType} onChange={onChange}
                            >
                                {/* Show the tenant-appropriate types; ensure the current value remains selectable when editing */}
                                {VISIBLE_EMAIL_TYPES.map(t => (
                                    <MenuItem key={t} value={t}>{t}</MenuItem>
                                ))}
                                {existing?.emailType && !VISIBLE_EMAIL_TYPES.includes(existing.emailType) && (
                                    <MenuItem key={existing.emailType} value={existing.emailType}>{existing.emailType}</MenuItem>
                                )}
                            </TextField>
                            <TextField
                                fullWidth margin="normal" label={t('replyTo')} name="replyTo"
                                value={form.replyTo} onChange={onChange} placeholder="info@jouwdomein.be"
                            />
                            <TextField
                                fullWidth required margin="normal" label={t('subject')} name="subject"
                                value={form.subject} onChange={onChange} inputProps={{maxLength: 180}}
                            />
                            {form.emailType?.endsWith('_REMINDER') && (<TextField
                                type={"number"} fullWidth required margin="normal" label={t('daysBeforeReservation')} name="daysBeforeReservation"
                                value={form.daysBeforeReservation} onChange={onChange} InputLabelProps={{shrink: true}} inputProps={{min: 0}}
                            />)}

                            <Box sx={{mt: 2, display: "flex", alignItems: "center", justifyContent: "space-between"}}>
                                <Typography variant="body2" sx={{fontWeight: 600}}>{t('bodyTextHtml')}</Typography>
                                <MenuButtonInsertPlaceholder
                                    t={t}
                                    editor={editor}
                                />
                            </Box>

                            <Box sx={{mt: 1}}>
                                {editor && (
                                    <RichTextEditorProvider editor={editor}>
                                        <RichTextField
                                            controls={
                                                <MenuControlsContainer>
                                                    <MenuSelectHeading/>
                                                    <MenuDivider/>
                                                    <MenuButtonBold/>
                                                    <MenuButtonItalic/>
                                                    <MenuButtonUnderline/>
                                                    <MenuDivider/>
                                                    <MenuButtonBulletedList/>
                                                    <MenuButtonOrderedList/>
                                                    <MenuDivider/>
                                                    <MenuButtonTextColor/>
                                                    <MenuDivider/>
                                                    <MenuButtonUndo/>
                                                    <MenuButtonRedo/>
                                                </MenuControlsContainer>
                                            }
                                        />
                                    </RichTextEditorProvider>
                                )}
                            </Box>
                        </Paper>
                    </Grid>

                    {/* Rechter kolom: live preview + sample values */}
                    <Grid item xs={12} md={6}>
                        <Paper variant="outlined" sx={{p: 2}}>
                            <Box sx={{display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1}}>
                                <Typography variant="subtitle2">{t('livePreview')}</Typography>
                            </Box>

                            <Grid container spacing={1}>
                                {VISIBLE_PARAMS.map((p) => (
                                    <Grid item xs={12} sm={6} key={p}>
                                        <TextField
                                            size="small" fullWidth label={p}
                                            value={previewValues[p] ?? ""}
                                            onChange={(e) => setPreviewValues(v => ({...v, [p]: e.target.value}))}
                                        />
                                    </Grid>
                                ))}
                            </Grid>

                            <Box sx={{mt: 2, p: 2, border: "1px solid", borderColor: "divider", borderRadius: 1, minHeight: 260, overflow: "auto"}}>
                                <div
                                    dangerouslySetInnerHTML={{
                                        __html: DOMPurify.sanitize(renderTemplate(form.body, previewValues)),
                                    }}
                                />
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>
            </Box>

            <AppSnackbar openSnackbar={openSnackbar}
                         snackbarMessage={snackbarMessage}
                         setOpenSnackbar={setOpenSnackbar}
                         severity={severity}/>

            <AlertDialogSlide
                title={alertTitle}
                message={alertMessage}
                openAlert={openAlert}
                action={alertAction}
                onClose={handleCloseAlert}
            />
        </Box>
    );
};

export default withAuthenticationRequired(EmailTemplateEditor, {
    onRedirecting: () => <Loading/>,
    returnTo: "/beheer/login"
});
